import React, { useState, useEffect } from "react";
import { Tooltip } from "./Tooltip";

interface TextIconButtonProps {
  icon: React.ReactNode;
  text: string;
  onClick: () => void;
  className?: string;
  style?: React.CSSProperties;
  showText?: boolean;
  tooltipText?: string;
  isScrollHidden?: boolean;
}

export function TextIconButton({
  icon,
  text,
  onClick,
  className = "",
  style = {},
  showText = false,
  tooltipText,
  isScrollHidden = false,
}: TextIconButtonProps) {
  const [tooltipPosition, setTooltipPosition] = useState<"bottom" | "left">(
    "bottom"
  );
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    const updateTooltipSettings = () => {
      const width = window.innerWidth;
      setTooltipPosition(width <= 1024 ? "left" : "bottom");
      setIsSmallScreen(width <= 1024);
    };

    updateTooltipSettings();
    window.addEventListener("resize", updateTooltipSettings);
    return () => window.removeEventListener("resize", updateTooltipSettings);
  }, []);

  // Track theme changes for donation button color inversion
  useEffect(() => {
    const checkTheme = () => {
      const theme = document.documentElement.getAttribute("data-theme");
      setIsDarkMode(theme !== "light");
    };

    checkTheme();

    const observer = new MutationObserver(checkTheme);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  // Check if this is a donation button and apply inverted colors
  const isDonationButton = className.includes("donation-button");

  // Inverted color scheme for donation button - white in dark mode, dark in light mode
  const getDonationButtonColors = () => {
    if (!isDonationButton) return null;

    const backgroundColor = isHovered
      ? isDarkMode
        ? "#f0f0f0"
        : "#1a2a3a"
      : isDarkMode
      ? "#ffffff"
      : "#111c28";

    const textColor = isDarkMode ? "#111c28" : "#ffffff";

    return {
      background: backgroundColor,
      color: textColor,
      boxShadow: isHovered
        ? isDarkMode
          ? "0 4px 16px rgba(255, 255, 255, 0.3)"
          : "0 4px 16px rgba(0, 0, 0, 0.3)"
        : "0 2px 8px rgba(0, 0, 0, 0.05)",
    };
  };

  const donationColors = getDonationButtonColors();

  const buttonContent = (
    <button
      className={`${className} ${isScrollHidden ? "scroll-hidden" : ""}`}
      onClick={onClick}
      style={{
        background: donationColors?.background || "var(--button-bg)",
        border: "none",
        borderRadius: showText ? "12px" : "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
        color: donationColors?.color || "var(--text-secondary)",
        backdropFilter: "blur(10px)",
        WebkitBackdropFilter: "blur(10px)",
        transition: "all 0.2s ease",
        padding: showText ? (isSmallScreen ? "6px 10px" : "8px 16px") : "0",
        gap: showText ? (isSmallScreen ? "6px" : "8px") : "0",
        minWidth: showText ? (isSmallScreen ? "100px" : "120px") : "48px",
        height: isSmallScreen && showText ? "40px" : "48px",
        fontSize: isSmallScreen && showText ? "16px" : "20px",
        boxShadow: donationColors?.boxShadow || undefined,
        ...style,
      }}
      onMouseEnter={(e) => {
        // Only apply hover effects on devices that actually support hover
        if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
          if (isDonationButton) {
            setIsHovered(true);
            const hoverColors = getDonationButtonColors();
            e.currentTarget.style.background =
              hoverColors?.background || "var(--button-hover-bg)";
            e.currentTarget.style.color =
              hoverColors?.color || "var(--button-hover-color)";
            e.currentTarget.style.boxShadow =
              hoverColors?.boxShadow || "0 4px 16px rgba(0, 0, 0, 0.3)";
          } else {
            e.currentTarget.style.background = "var(--button-hover-bg)";
            e.currentTarget.style.color = "var(--button-hover-color)";
          }
          e.currentTarget.style.transform = "scale(1.05)";
        }
      }}
      onMouseLeave={(e) => {
        // Only apply hover effects on devices that actually support hover
        if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
          if (isDonationButton) {
            setIsHovered(false);
            const normalColors = getDonationButtonColors();
            e.currentTarget.style.background =
              normalColors?.background || "var(--button-bg)";
            e.currentTarget.style.color =
              normalColors?.color || "var(--text-secondary)";
            e.currentTarget.style.boxShadow =
              normalColors?.boxShadow || "0 2px 8px rgba(0, 0, 0, 0.05)";
          } else {
            e.currentTarget.style.background = "var(--button-bg)";
            e.currentTarget.style.color = "var(--text-secondary)";
          }
          e.currentTarget.style.transform = "scale(1)";
        }
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {icon}
      </div>
      {showText && (
        <span
          style={{
            fontSize: isSmallScreen ? "12px" : "14px",
            fontWeight: "500",
            whiteSpace: "nowrap",
            userSelect: "none",
          }}
        >
          {text}
        </span>
      )}
    </button>
  );

  // Only show tooltip when not showing text (to avoid redundancy)
  if (showText || !tooltipText) {
    return buttonContent;
  }

  return (
    <Tooltip
      text={tooltipText}
      position={tooltipPosition}
      delay={250}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
      }}
    >
      {buttonContent}
    </Tooltip>
  );
}
